import React, { useState, useEffect, useRef } from 'react';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./HeroCarousel.css"

import CountdownTimer from './CountdownTimer';
import EventStats from './EventStats';
import { useUser } from '@/contexts/UserContext';
import type { Countdown } from '@/types/common';
import type { Event } from '@/types/event';
import { imageSrc } from '@/utils/event';

interface HeroCarouselProps {
  featuredEvents: Event[];
  onLearnMore: (event: Event) => void;
  loading?: boolean|undefined;
  quota?: any;
}

const HeroCarousel: React.FC<HeroCarouselProps> = ({ featuredEvents, onLearnMore, loading, quota: myQuotas = {} }) => {
  const [timeLeftMap, setTimeLeftMap] = useState<Record<string, Countdown>>({});
  const [slickSlider, setSlickSlider] = useState<Slider>(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const titleRefs = useRef<(HTMLHeadingElement | null)[]>([]);

  const { user } = useUser();
  const isAuthenticated = !!user?.isAuthenticated;
  
  // Check if title overflows - using refs instead of state
  const checkTitleOverflow = (element: HTMLHeadingElement | null) => {
    if (!element) return false;
    return element.scrollHeight > element.clientHeight;
  };

  // Handle slide change
  const handleSlideChange = (currentSlide: number) => {
    setActiveSlideIndex(currentSlide);
  };

  // Calculate countdown for each individual event
  useEffect(() => {
    if (!featuredEvents.length) return;

    const updateCountdowns = () => {
      const now = Date.now();
      const newTimeLeftMap: Record<string, Countdown> = {};

      featuredEvents.forEach(event => {
        const targetDate = new Date(event.event_date as string);
        const diff = targetDate.getTime() - now;

        if (diff <= 0) {
          newTimeLeftMap[event.id] = { days: 0, hours: 0, minutes: 0, seconds: 0, milliseconds: 0 };
          return;
        }

        newTimeLeftMap[event.id] = {
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((diff / (1000 * 60)) % 60),
          seconds: Math.floor((diff / 1000) % 60),
          milliseconds: diff % 1000,
        };
      });

      setTimeLeftMap(newTimeLeftMap);
    };

    updateCountdowns();
    const interval = setInterval(updateCountdowns, 100);
    return () => clearInterval(interval);
  }, [featuredEvents]);

  const sliderSettings = {
    autoplay: true,
    autoplaySpeed: 5000,
    dots: true,
    infinite: true,
    arrows: true,
    pauseOnHover: true,
    fade: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    afterChange: handleSlideChange,
    appendDots: dots => (
      <div className="slick-dots appendDots">
        <ul className="relative w-full bottom-5 left-1/2 transform -translate-x-1/2 z-50 bg-transparent flex items-center justify-center space-x-2 mt-4">{dots}</ul>
      </div>
    ),
    customPaging: i => (
      <div className="absolute w-2 h-2 bg-white/30 rounded-full transition-all duration-300 customPaging"></div>
    ),
  };

  if (loading || (loading === undefined && !featuredEvents.length)) {
    return (
      <section className="mt-5 md:mx-6 relative h-[60vh] md:min-h-[500px] overflow-hidden bg-transparent rounded-2xl flex items-center justify-center">       
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </section>
    );
  }

  if (!featuredEvents.length) {
    return (
      <section className="mt-5 md:mx-6 relative h-[60vh] md:min-h-[500px] overflow-hidden bg-transparent rounded-2xl flex items-center justify-center">
        <div className="text-center text-gray-400">
          <p>No featured events available</p>
        </div>
      </section>
    );
  }

  return (
    <section className="hero-carousel-section mt-2 md:mt-5 md:mx-6 relative min-h-[60vh] md:min-h-[500px] bg-transparent rounded-2xl">
      <Slider
        ref={slider => setSlickSlider(slider)}
        {...sliderSettings}>
        {featuredEvents.map((event, index) => {
          const bg = imageSrc(event);
          const currentTimeLeft = timeLeftMap[event.id] || { days: 0, hours: 0, minutes: 0, seconds: 0, milliseconds: 0 };
          const tag = event.taggings?.[0]?.name || null;
          const category = tag || event.category as string || null;
          const type = tag || category || event.type || null;
          
          return (
            <div key={event.id} className="relative rounded-2xl overflow-hidden hero-carousel-bg-slide">
              {/* Background Image - Cover full slider */}
              <div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat rounded-2xl"
                style={{ 
                  backgroundImage: `url(${bg})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <div className="absolute inset-0 bg-black/50 rounded-2xl" />
              
              {/* Event Info - Full width between arrows */}
              <div className="relative z-10 min-h-[70vh] md:min-h-[500px] flex items-center justify-center py-4 md:py-0">
                <div className="container mx-auto px-12 md:px-16 w-full">
                  <div className="text-white text-start max-w-4xl mx-auto">
                    <div className="inline-block bg-orange-400/20 px-2 py-0.5 sm:px-4 sm:py-1 rounded-full border border-orange-400/50 text-sm font-light mb-2 sm:text-base sm:font-normal sm:mb-4">
                      <span className="w-2 h-2 rounded-full bg-orange-300 inline-block"></span>
                      <span className="text-orange-300 ml-2">Featured Event</span>                          
                    </div>
                    
                    <div className="mb-6">
                      <div className="text-sm md:text-base font-bold text-orange-300 mb-3 uppercase tracking-wide">
                        {type}
                      </div>
                      
                      {/* Title with auto-scroll on hover if overflowing */}
                      <div className="mb-4 max-h-[120px] overflow-hidden">
                        <h1 
                          ref={el => {
                            if(titleRefs) titleRefs.current[index] = el;
                          }}
                          className="text-2xl md:text-4xl font-bold leading-tight title-text"
                          style={{ 
                            transition: 'transform 0.3s ease',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            const element = e.currentTarget;
                            if (checkTitleOverflow(element)) {
                              const scrollDistance = element.scrollHeight - element.clientHeight;
                              element.style.transform = `translateY(-${scrollDistance}px)`;
                              element.style.transition = 'transform 6s linear';
                            }
                          }}
                          onMouseLeave={(e) => {
                            const element = e.currentTarget;
                            element.style.transform = 'translateY(0)';
                            element.style.transition = 'transform 0.5s ease';
                          }}
                        >
                          {event.title}
                        </h1>
                      </div>
                    </div>

                    <EventStats event={event} />

                    <CountdownTimer timeLeft={currentTimeLeft} />

                    <div className="text-start mt-6">
                      <button 
                        onClick={() => onLearnMore(event)} 
                        className="px-3 py-0 mb-4 text-sm font-medium sm:px-6 sm:py-3 sm:text-base sm:font-medium bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white hover:bg-white/20 transition-all hover:scale-105"
                      >
                        Learn More
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </Slider>
    </section>
  );
};

export default HeroCarousel;